import { useState, useRef } from "react";
import { X, Upload, Download } from "lucide-react";
import { Button } from "antd";
import axios from "axios";
import { useDispatch, useSelector } from "react-redux";
import { downloadMemberSampleFileApi } from "../../../../redux/action/Sample/Member";
import api from "../../../../redux/instance/api";
import {
  errorBulkUploadMember,
  startBulkUploadMember,
  successBulkUploadMember,
} from "../../../../redux/slice/BulkUpload/Member";

export default function MemberUpload({ isOpen, onClose }) {
  const [files, setFiles] = useState([]);
  const dispatch = useDispatch();

  const { memeberSampleLoading } = useSelector(
    (state) => state.DOWNLOAD_MEMBER_SAMPLE_FILE
  );

  const [isDragOver, setIsDragOver] = useState(false);

  const fileInputRef = useRef(null);
  // Tracks in-flight AbortControllers per file id so Cancel/X can actually stop the request.
  const abortControllersRef = useRef(new Map());

  const downloadSample = () => {
    try {
      dispatch(downloadMemberSampleFileApi());
    } catch (error) {
      console.log("error==========>", error);
    }
  };

  const handleFileSelect = (selectedFiles) => {
    if (!selectedFiles) return;

    const validFiles = [];
    const rejectedFiles = [];

    Array.from(selectedFiles).forEach((file, index) => {
      const fileSizeMB = file.size / (1024 * 1024);
      const fileExtension = file.name.split(".").pop()?.toLowerCase();

      // Validate file type
      if (!fileExtension || !["csv", "xlsx", "xls"].includes(fileExtension)) {
        rejectedFiles.push(
          `${file.name} - Invalid file type. Only CSV and Excel files are allowed.`
        );
        return;
      }

      // Validate file size (5MB limit)
      if (fileSizeMB > 5) {
        rejectedFiles.push(
          `${file.name} - File size exceeds 5MB limit (${fileSizeMB.toFixed(
            1
          )}MB)`
        );
        return;
      }

      // Stage the file only - the actual upload happens when "Upload File" is clicked
      validFiles.push({
        id: Date.now().toString() + index,
        name: file.name,
        size: `${fileSizeMB.toFixed(1)} MB`,
        progress: 0,
        status: "pending",
        fileObject: file,
      });
    });

    // Show rejected files alert if any
    if (rejectedFiles.length > 0) {
      alert(`Some files were rejected:\n\n${rejectedFiles.join("\n")}`);
    }

    if (validFiles.length > 0) {
      setFiles((prev) => [...prev, ...validFiles]);
    }

    // Allow selecting the same file again after it is removed/re-added
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const uploadFile = (fileId, fileObject) => {
    const formData = new FormData();
    formData.append("file", fileObject);

    const controller = new AbortController();
    abortControllersRef.current.set(fileId, controller);

    dispatch(startBulkUploadMember());

    setFiles((prev) =>
      prev.map((file) =>
        file.id === fileId
          ? { ...file, status: "uploading", progress: 0 }
          : file
      )
    );

    api
      .post(`client/upload`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
        signal: controller.signal,
        onUploadProgress: (progressEvent) => {
          const progress = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );

          setFiles((prev) =>
            prev.map((file) =>
              file.id === fileId ? { ...file, progress } : file
            )
          );
        },
      })
      .then((res) => {
        dispatch(successBulkUploadMember(res.data));

        const result = res.data.payload;
        const isFailed = result.success_count === 0 && result.failure_count > 0;

        setFiles((prev) =>
          prev.map((file) =>
            file.id === fileId
              ? {
                  ...file,
                  progress: 100,
                  status: isFailed ? "failed" : "completed",
                  successCount: result.success_count,
                  failureCount: result.failure_count,
                  totalCount: result.total_count,
                  failureFile: result.failure_file_name,
                  issueMessage: null,
                }
              : file
          )
        );
      })
      .catch((error) => {
        if (axios.isCancel(error)) {
          // Upload was cancelled by the user - drop it from the list entirely
          setFiles((prev) => prev.filter((file) => file.id !== fileId));
          return;
        }

        dispatch(errorBulkUploadMember(error?.response?.data?.message));
        setFiles((prev) =>
          prev.map((file) =>
            file.id === fileId
              ? {
                  ...file,
                  status: "failed",
                  issueMessage:
                    error?.response?.data?.message ||
                    "Something went wrong while uploading this file.",
                }
              : file
          )
        );
      })
      .finally(() => {
        abortControllersRef.current.delete(fileId);
      });
  };

  const handleUploadFiles = () => {
    const pendingFiles = files.filter((file) => file.status === "pending");
    pendingFiles.forEach((file) => uploadFile(file.id, file.fileObject));
  };

  const getFileIcon = (fileName) => {
    const extension = fileName.split(".").pop()?.toLowerCase();

    if (extension === "csv") {
      return (
        <div className="w-8 h-8 bg-green-600 rounded flex items-center justify-center">
          <span className="text-white text-xs font-bold">CSV</span>
        </div>
      );
    } else if (extension === "xlsx" || extension === "xls") {
      return (
        <div className="w-8 h-8 bg-green-700 rounded flex items-center justify-center">
          <span className="text-white text-xs font-bold">XLS</span>
        </div>
      );
    }

    return (
      <div className="w-8 h-8 bg-gray-600 rounded flex items-center justify-center">
        <span className="text-white text-xs font-medium">?</span>
      </div>
    );
  };

  const handleChooseFiles = () => {
    fileInputRef.current?.click();
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    const droppedFiles = e.dataTransfer.files;
    handleFileSelect(droppedFiles);
  };

  // Removes a file that hasn't started uploading yet, or one that already finished/failed
  const removeFile = (id) => {
    setFiles((prev) => prev.filter((file) => file.id !== id));
  };

  // Aborts an in-flight upload so the request is actually stopped, not just hidden
  const cancelUpload = (id) => {
    const controller = abortControllersRef.current.get(id);
    if (controller) {
      controller.abort();
      abortControllersRef.current.delete(id);
    }
    setFiles((prev) => prev.filter((file) => file.id !== id));
  };

  // Aborts every in-flight upload and clears local state so re-opening the modal starts fresh
  const resetState = () => {
    abortControllersRef.current.forEach((controller) => controller.abort());
    abortControllersRef.current.clear();
    setFiles([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleCancel = () => {
    resetState();
    onClose();
  };

  const hasPendingFiles = files.some((file) => file.status === "pending");
  const isUploading = files.some((file) => file.status === "uploading");

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 font-poppins">
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[calc(90*var(--vh))] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-5 pb-3 border-b-[1px] border-solid border-x-0 border-t-0 border-[#E2E8F0]">
          <div>
            <h2 className="text-2xl font-bold text-[#171717] font-poppins mb-1">
              Bulk Upload Members
            </h2>
            <p className="mt-0 mb-0 text-sm text-[#64748B]">
              Easily upload multiple members using a CSV file.
            </p>
          </div>
          <button
            onClick={handleCancel}
            className="text-gray-400 hover:text-gray-600 bg-transparent border-0 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6">
          {/* Drag & Drop Area */}
          <div
            className={`border-2 border-dashed rounded-2xl p-12 text-center transition-colors ${
              isDragOver
                ? "border-[#2563EB] bg-[#EFF6FF]"
                : "border-[#E2E8F0] bg-[#F8FAFC]"
            }`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <div className="flex flex-col items-center space-y-4">
              <div className="w-14 h-14 bg-[#EFF6FF] rounded-xl flex items-center justify-center">
                <Upload className="w-7 h-7 text-[#2563EB]" />
              </div>
              <div>
                <p className="text-base font-semibold text-[#171717] mb-0">
                  <span className="text-[#2563EB]">
                    Drag & Drop
                  </span>{" "}
                  your files here
                </p>
                <p className="text-[#64748B] text-[13px] mt-1 mb-0">
                  Accepted formats: .csv, .xlsx | Max Size: 5 MB
                </p>
              </div>
              <Button
                variant="secondary"
                className="h-9 px-[14px] bg-white border-[1px] border-solid border-[#E2E8F0] rounded-xl shadow-sm text-[#171717] text-[13px] font-semibold font-poppins hover:bg-[#F8FAFC]"
                onClick={handleChooseFiles}
              >
                Choose Files
              </Button>
            </div>
          </div>

          {/* Hidden File Input */}
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept=".csv,.xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,text/csv"
            onChange={(e) => handleFileSelect(e.target.files)}
            className="hidden"
          />

          {/* File List */}
          {files.length > 0 && (
            <div className="mt-6 space-y-3">
              {files
                .slice()
                .reverse()
                .map((file) => (
                  <div key={file.id} className="bg-white border-[1px] border-solid border-[#E2E8F0] shadow-sm rounded-2xl p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center space-x-3 w-3/4">
                        {getFileIcon(file.name)}
                        <div>
                          <span className="font-medium text-[#171717]">
                            {file.name}
                          </span>
                          <span className="text-sm text-[#64748B]">
                            {file.status === "pending" && (
                              <div className="flex">
                                <span className="text-[#64748B]">
                                  Ready to upload
                                </span>
                              </div>
                            )}
                            {file.status === "failed" && (
                              <div className="flex">
                                <span className="text-red-500">
                                  {file.issueMessage
                                    ? file.issueMessage
                                    : "There is an issue with your file."}{" "}
                                  {file.failureFile && (
                                    <a
                                      href={file.failureFile}
                                      target="_blank"
                                      rel="noreferrer"
                                      className="underline"
                                    >
                                      Download Failure Report
                                    </a>
                                  )}
                                </span>
                              </div>
                            )}
                            {file.status === "completed" && (
                              <div className="flex">
                                <span className="text-green-500">
                                  {typeof file.successCount === "number" ? (
                                    <>
                                      {file.successCount} of{" "}
                                      {file.totalCount ??
                                        file.successCount +
                                          (file.failureCount || 0)}{" "}
                                      members uploaded successfully
                                      {file.failureCount > 0 && (
                                        <>
                                          , {file.failureCount} failed.{" "}
                                          {file.failureFile && (
                                            <a
                                              href={file.failureFile}
                                              target="_blank"
                                              rel="noreferrer"
                                              className="underline text-red-500"
                                            >
                                              Download Failure Report
                                            </a>
                                          )}
                                        </>
                                      )}
                                    </>
                                  ) : (
                                    "Your member list has been successfully uploaded"
                                  )}
                                </span>
                              </div>
                            )}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center space-x-3">
                        {file.status === "pending" && (
                          <button
                            onClick={() => removeFile(file.id)}
                            className="text-gray-400 hover:text-gray-600 bg-transparent border-0"
                          >
                            <X className="w-5 h-5" />
                          </button>
                        )}
                        {file.status === "uploading" && (
                          <>
                            <span className="text-sm font-semibold text-[#334155]">
                              {file.progress}%
                            </span>
                            <button
                              onClick={() => cancelUpload(file.id)}
                              className="text-gray-400 hover:text-gray-600 bg-transparent border-0"
                            >
                              <X className="w-5 h-5" />
                            </button>
                          </>
                        )}
                        {file.status === "completed" && (
                          <>
                            <span className="text-sm font-medium text-green-600">
                              Completed
                            </span>
                            <button
                              onClick={() => removeFile(file.id)}
                              className="text-gray-400 hover:text-gray-600 bg-transparent border-0"
                            >
                              <X className="w-5 h-5" />
                            </button>
                          </>
                        )}
                        {file.status === "failed" && (
                          <>
                            <span className="text-sm font-medium text-red-600">
                              Failed
                            </span>
                            <button
                              onClick={() => removeFile(file.id)}
                              className="text-gray-400 hover:text-gray-600 bg-transparent border-0"
                            >
                              <X className="w-5 h-5" />
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                    {/* Progress Bar */}
                    <div className="w-full bg-[#F1F5F9] rounded-full h-2">
                      <div
                        className={`h-2 rounded-full transition-all duration-300 ${
                          file.status === "completed"
                            ? "bg-green-500"
                            : file.status === "failed"
                            ? "bg-red-500"
                            : file.status === "pending"
                            ? "bg-[#CBD5E1]"
                            : "bg-[#2563EB]"
                        }`}
                        style={{ width: `${file.progress}%` }}
                      />
                    </div>
                  </div>
                ))}
            </div>
          )}

          {/* Download Sample Template */}
          <div className="mt-6 p-4 bg-[#F8FAFC] border-[1px] border-solid border-[#E2E8F0] rounded-2xl">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="font-semibold text-base text-[#171717] mb-0">
                  Download Sample Template
                </p>
                <p className="text-[#64748B] text-[13px] mt-0.5 mb-0">
                  Make sure the data matches the sample format provided.
                </p>
              </div>
              <Button
                variant="outline"
                className="border-0 hover:bg-[#EFF6FF] bg-transparent text-[13px] text-[#2563EB] font-semibold font-poppins rounded-xl"
                loading={memeberSampleLoading}
                onClick={() => downloadSample()}
              >
                <Download className="w-4 h-4 mr-2" color="#2563EB" />
                Download Sample
              </Button>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-4 mt-6">
            <Button
              variant="outline"
              onClick={handleCancel}
              className="flex-1 h-auto py-3 border-[1px] border-solid border-[#E2E8F0] rounded-xl text-base font-semibold text-[#334155] bg-white hover:bg-gray-50 font-poppins"
            >
              Cancel
            </Button>
            <Button
              className="flex-1 h-auto py-3 btn-gradient border-0 rounded-xl text-base font-semibold font-poppins"
              onClick={handleUploadFiles}
              disabled={!hasPendingFiles}
              loading={isUploading}
            >
              Upload File
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
